import React, { useEffect, useState } from "react";
import styles from "./forgot-password.module.css";
import { API } from "@/lib/api";
import { useLocation } from "wouter";
import TextInput from "@/components/forms/TextInput/TextInput";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Flex from "@/components/Flex/Flex";
import Link from "@/components/Link/Link";
import Button from "@/components/forms/Button/Button";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";

type ForgotPasswordForm = {
  email: string;
};

export default function ForgotPasswordPage() {
  const [location, navigate] = useLocation();
  const { showNotification } = useNotificationContext();
  const [forgotPasswordForm, setForgotPasswordForm] = useState<ForgotPasswordForm>({
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState<string>("");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);

  const validateInputs = () => {
    let valid = true;

    //Email rules
    if (forgotPasswordForm.email === "") {
      setEmailErrorText("Email address is required.");
      valid = false;
    }
    if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(forgotPasswordForm.email)) {
      setEmailErrorText("Invalid email address.");
      valid = false;
    }

    //If the form was invalid then disable the submit button.
    if (!valid) {
      setSubmitButtonDisabled(true);
    }

    return valid;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const inputsAreValid = validateInputs();

    //If inputs aren't valid then don't continue with submit.
    if (!inputsAreValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      const lowered = forgotPasswordForm.email.toLowerCase();
      await API.forgotPassword({ email: lowered });
      navigate("~/auth/reset-email-sent?email=" + lowered);
    } catch (err) {
      showNotification("Failed to send forgot password email!", NotificationType.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  //Handle email change
  const onEmailChange = (email: string) => {
    setForgotPasswordForm({...forgotPasswordForm, email });
    setEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Cleanup form once comp is unmounted
  useEffect(() => {
    return () => {
      setForgotPasswordForm({
        email: "",
      });
    };
  }, []);

  return (
    <div className={styles.formContainer}>
      <div style={{margin:"2rem 0"}}>
        <h3>Forgot password</h3>
        <p>
          {"Enter the email address and we'll email you instructions with a link to reset your password."}
        </p>
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="email">
          <Flex gap={1.5} align="center">
            <span>Email address</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="email"
            inputId="email"
            placeholder="your@email.com"
            value={forgotPasswordForm.email}
            onChange={onEmailChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {emailErrorText && <p className={`small-text ${styles.invalidText}`}>{emailErrorText}</p>}
      </div>

      <div>
        <Button
          type="primary"
          disabled={isSubmitting || submitButtonDisabled}
          className={styles.submitButton}
          onClick={handleSubmit}
        >
          {isSubmitting ? <LoadingSpinner /> : <span>Reset my password</span>}
        </Button>

        <div className={styles.space}></div>

        <Flex justify="center">
          <span>
            <Link href="/auth/login">Return to Log in</Link>
          </span>
        </Flex>
      </div>
    </div>
  );
}
